import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";

const KNOWN_METALS = ["Silver", "9ct Yellow Gold", "9ct White Gold", "9ct Rose Gold", "18ct Yellow Gold", "18ct White Gold", "18ct Rose Gold", "22ct Yellow Gold", "Platinum"];

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [metals, setMetals] = useState(KNOWN_METALS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/filters").then(({ data }) => {
      setMetals([...new Set([...KNOWN_METALS, ...data.metals])]);
    }).catch(() => {});
  }, []);

  if (!settings) return <p className="text-stone-mist text-sm">Loading...</p>;

  const toggle = (m) =>
    setSettings((s) => ({
      ...s,
      stock_metals: s.stock_metals.includes(m) ? s.stock_metals.filter((x) => x !== m) : [...s.stock_metals, m],
    }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", settings);
      toast.success("Stock settings saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-10" data-testid="admin-settings">
      <div>
        <h2 className="text-xs uppercase tracking-[0.25em] text-gold mb-2 font-medium">In-Stock Metals</h2>
        <p className="text-stone-mist text-sm font-light mb-6">
          Variations in these metals show as "In Stock". All other metals show as "Made to Order". Prices are never
          changed by these settings.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {metals.map((m) => (
            <label
              key={m}
              className={`flex items-center gap-3 border px-4 py-3 cursor-pointer text-sm transition-colors ${
                settings.stock_metals.includes(m) ? "border-gold text-ivory bg-gold/5" : "border-white/10 text-stone-mist hover:border-gold/40"
              }`}
              data-testid={`stock-metal-${m.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <input type="checkbox" checked={settings.stock_metals.includes(m)} onChange={() => toggle(m)} className="accent-[#C5A059]" />
              {m}
            </label>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-xs uppercase tracking-[0.25em] text-gold mb-4 font-medium">Made-to-Order Lead Time</h2>
        <input
          value={settings.lead_time}
          onChange={(e) => setSettings((s) => ({ ...s, lead_time: e.target.value }))}
          className="w-full bg-ink border border-white/10 focus:border-gold outline-none px-4 py-3 text-sm text-ivory transition-colors"
          data-testid="lead-time-input"
        />
        <p className="text-stone-mist text-xs mt-2">Shown to customers as: "Made to Order — {settings.lead_time}"</p>
      </div>

      <button onClick={save} disabled={saving} data-testid="settings-save" className="bg-gold text-ink px-8 py-3 text-xs uppercase tracking-[0.25em] font-medium hover:bg-gold-glow transition-colors disabled:opacity-50">
        {saving ? "Saving..." : "Save Settings"}
      </button>
    </div>
  );
}
